import { PhotoQueueItemStates } from "../config/PhotoConfigs.js";

/**
 * Whether a review/queue file can still receive tags on the Nahrát screen.
 *
 * @param {object} [item]
 * @returns {boolean}
 */
export function isPhotoQueueItemTaggable(item) {
  return item?.state === PhotoQueueItemStates.QUEUED && !item?.skipReason;
}

/**
 * Whether a review-tree folder (or any descendant) still has a taggable file.
 *
 * @param {{ files?: object[], folders?: Map<string, object>|Record<string, object> }} [folder]
 * @returns {boolean}
 */
export function folderHasTaggablePhotos(folder) {
  if (!folder) {
    return false;
  }
  for (const item of folder.files ?? []) {
    if (isPhotoQueueItemTaggable(item)) {
      return true;
    }
  }
  const nested = folder.folders;
  if (!nested) {
    return false;
  }
  const children = nested instanceof Map ? nested.values() : Object.values(nested);
  for (const child of children) {
    if (folderHasTaggablePhotos(child)) {
      return true;
    }
  }
  return false;
}

/**
 * Normalizes photo tags: trim, drop empties, de-dupe (first occurrence wins).
 *
 * @param {unknown} tags
 * @returns {string[]}
 */
export function normalizePhotoTags(tags) {
  if (!Array.isArray(tags)) {
    return [];
  }
  const seen = new Set();
  const result = [];
  for (const raw of tags) {
    const value = String(raw ?? "").trim();
    if (!value || seen.has(value)) {
      continue;
    }
    seen.add(value);
    result.push(value);
  }
  return result;
}

/**
 * Union of tag lists with backend-compatible normalization.
 *
 * @param {...unknown} tagLists
 * @returns {string[]}
 */
export function unionTags(...tagLists) {
  return normalizePhotoTags(tagLists.flat());
}

/**
 * @param {Record<string, unknown>|Map<string, unknown>|undefined} folderTagsByPath
 * @param {string} path
 * @returns {unknown}
 */
function tagsAtPath(folderTagsByPath, path) {
  if (!folderTagsByPath) {
    return undefined;
  }
  if (folderTagsByPath instanceof Map) {
    return folderTagsByPath.get(path);
  }
  return folderTagsByPath[path];
}

/**
 * Unions tags on every ancestor folder of `filePath` plus tags stored on the file path itself.
 *
 * @param {Record<string, string[]>|Map<string, string[]>|undefined} folderTagsByPath
 * @param {string} filePath
 * @returns {string[]}
 */
export function inheritFolderTags(folderTagsByPath, filePath) {
  const normalized = String(filePath ?? "")
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");
  const parts = normalized.split("/").filter(Boolean);
  const collected = [];
  let current = "";
  for (const part of parts) {
    current = current ? `${current}/${part}` : part;
    collected.push(tagsAtPath(folderTagsByPath, current));
  }
  return unionTags(...collected);
}

/**
 * Tags sent on `photo/create`: session-wide chips, then inherited folder tags, then file chips.
 *
 * @param {object} [item]
 * @param {Record<string, string[]>|Map<string, string[]>|undefined} folderTagsByPath
 * @param {unknown} [sessionTags]
 * @returns {string[]}
 */
export function resolveQueueItemTags(item, folderTagsByPath, sessionTags) {
  const tagsByPath = { ...(folderTagsByPath ?? {}), [item?.filename]: item?.tags };
  return unionTags(sessionTags, inheritFolderTags(tagsByPath, item?.filename));
}

/**
 * Adds tags onto an existing list (union, normalized).
 *
 * @param {unknown} existingTags
 * @param {unknown} addTags
 * @returns {string[]}
 */
export function applyTagManagerAdd(existingTags, addTags) {
  return unionTags(existingTags, addTags);
}

/**
 * Removes tags from an existing list (normalized match).
 *
 * @param {unknown} existingTags
 * @param {unknown} removeTags
 * @returns {string[]}
 */
export function applyTagManagerRemove(existingTags, removeTags) {
  const remove = new Set(normalizePhotoTags(removeTags));
  return normalizePhotoTags(existingTags).filter((tag) => !remove.has(tag));
}

/** Thumb (48) + cell padding; keep rows tall enough for the filename + preview. */
export const PHOTO_LIBRARY_ROW_MIN_HEIGHT = 72;

const CHIP_HEIGHT = 24;
const CHIP_GAP = 4;
const CHIP_HORIZONTAL_PAD = 28;
const CHAR_PX = 8;
const INPUT_SLOT_PX = 64;
const COLUMN_INSET = 20;
const VERTICAL_CHROME = 30;

/**
 * @param {string} tag
 * @param {number} usableWidth
 * @returns {number}
 */
function estimateChipWidth(tag, usableWidth) {
  return Math.min(usableWidth, CHIP_HORIZONTAL_PAD + String(tag).length * CHAR_PX);
}

/**
 * Numeric library-grid row height from tags + column width.
 * Avoids DataGrid `getRowHeight: "auto"` which measures Autocomplete cells and lags.
 *
 * @param {unknown} tags
 * @param {number} [columnWidth]
 * @returns {number}
 */
export function estimatePhotoLibraryRowHeight(tags, columnWidth = 280) {
  const list = Array.isArray(tags) ? tags : [];
  const usable = Math.max(80, Number(columnWidth) || 280) - COLUMN_INSET;
  const widths = [...list.map((tag) => estimateChipWidth(tag, usable)), INPUT_SLOT_PX];

  let lines = 1;
  let used = 0;
  for (const width of widths) {
    const piece = Math.min(usable, width);
    if (used > 0 && used + CHIP_GAP + piece > usable) {
      lines += 1;
      used = piece;
    } else {
      used += (used > 0 ? CHIP_GAP : 0) + piece;
    }
  }

  const content = VERTICAL_CHROME + lines * CHIP_HEIGHT + (lines - 1) * CHIP_GAP;
  return Math.max(PHOTO_LIBRARY_ROW_MIN_HEIGHT, content);
}
